import { type EnumStorageTransactionType } from '@Model';
import { useOnTheFlyModal } from '@synqo/frontend-component-library';
import { useCallback } from 'react';
import { BookingFlow } from './BookingFlow';
import { FrameEditStorageTransaction } from './FrameEditStorageTransaction';
import { usePrepareBookingDraft } from './usePrepareBookingDraft';

export interface OpenBookingOptions {
    storageLocationId?: string;
    materialId?: string;
    type?: EnumStorageTransactionType;
    /** Called after every booking made in the mask. */
    onBooked?: () => void;
}

export interface OpenViewOptions {
    /** Called after the booking shown was cancelled. */
    onCancelled?: () => void;
}

/** Opens the booking mask and the read-only view of a stored booking, each in an overlay. */
// @ai-generated 2026-10-23T17:40:00+00:00 by matthias.gessinger@synqony.com using Claude Sonnet 5.5
export function useOpenStorageTransaction() {
    const { openOnTheFlyModal, closeOnTheFlyModals } = useOnTheFlyModal();
    const prepareDraft = usePrepareBookingDraft();

    const openBooking = useCallback(
        async ({ storageLocationId, materialId, type, onBooked }: OpenBookingOptions = {}) => {
            const draft = await prepareDraft({ storageLocationId, materialId, type });
            if (draft === undefined) {
                return;
            }

            let modalId = '';
            modalId = openOnTheFlyModal({
                label: 'Lagerbuchung',
                child: (
                    <BookingFlow
                        initialDraftId={draft.id}
                        focusMaterial={storageLocationId !== undefined && materialId === undefined}
                        onBooked={onBooked}
                        onClose={() => closeOnTheFlyModals(modalId)}
                    />
                ),
            });
        },
        [prepareDraft, openOnTheFlyModal, closeOnTheFlyModals],
    );

    const openView = useCallback(
        (bookingId: string, { onCancelled }: OpenViewOptions = {}) => {
            let modalId = '';
            modalId = openOnTheFlyModal({
                label: 'Lagerbuchung',
                child: (
                    <FrameEditStorageTransaction
                        primaryKey={bookingId}
                        embedded
                        canEdit={false}
                        canCreate={false}
                        canDelete={false}
                        onCancelled={onCancelled}
                        events={{ onBack: undefined, onAdd: undefined, onClose: () => closeOnTheFlyModals(modalId) }}
                    />
                ),
            });
        },
        [openOnTheFlyModal, closeOnTheFlyModals],
    );

    return { openBooking, openView };
}
